import { Link } from "react-router";

import { useQuery } from "@tanstack/react-query";

import { AlertTriangleIcon, LockIcon, LogOutIcon, MailIcon, ShieldCheckIcon, UserIcon } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

import DeleteAccountForm from "@/features/account/components/DeleteAccountForm";
import UpdateEmailAddressForm from "@/features/account/components/UpdateEmailAddressForm";
import UpdatePasswordForm from "@/features/account/components/UpdatePasswordForm";
import UpdateProfileForm from "@/features/account/components/UpdateProfileForm";
import { getMyAccountOverview } from "@/features/account/services/account";
import { AccountOverview } from "@/features/account/types";
import Error from "@/shared/components/Error";
import { useErrorModal } from "@/shared/hooks/useErrorModal";
import { useLogout } from "@/shared/hooks/useLogout";
import { QUERY_EXPIRATIONS, QUERY_KEYS } from "@/shared/lib/constants/queries";
import { getMyDetails } from "@/shared/lib/services/user";
import { User } from "@/shared/lib/types/user";
import { AppError } from "@/shared/lib/utils/errors";

interface SettingsSectionProps {
  title: string;
  description: string;
  children: React.ReactNode;
}

function SettingsSection({ title, description, children }: SettingsSectionProps) {
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[280px_1fr]">
      <div className="flex flex-col gap-1.5">
        <h3 className="text-sm font-semibold">{title}</h3>
        <p className="text-xs leading-relaxed text-muted-foreground">{description}</p>
      </div>
      <div className="flex flex-col gap-4">{children}</div>
    </div>
  );
}

function SettingsFormSkeleton() {
  return (
    <Card>
      <CardContent className="flex flex-col gap-6">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-8 w-32 self-end" />
      </CardContent>
    </Card>
  );
}

export default function Settings() {
  const { logout, isLoggingOut } = useLogout();

  const {
    data: user,
    isPending,
    error,
    refetch,
  } = useQuery<User, AppError | Error>({
    queryKey: [QUERY_KEYS.GET_MY_DETAILS],
    queryFn: getMyDetails,
    staleTime: QUERY_EXPIRATIONS.GET_MY_DETAILS,
  });

  const { data: overview } = useQuery<AccountOverview, AppError | Error>({
    queryKey: [QUERY_KEYS.GET_MY_ACCOUNT_OVERVIEW],
    queryFn: getMyAccountOverview,
    staleTime: QUERY_EXPIRATIONS.GET_MY_ACCOUNT_OVERVIEW,
  });

  const { open: errorModalOpen, setOpen: setErrorModalOpen, handleRetry } = useErrorModal(error, refetch);

  return (
    <div className="flex flex-col gap-10">
      {/* Page header */}
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold">Account settings</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage your personal details, sign in credentials and account status. Looking for your activity?{" "}
            <Link to="/profile" className="underline underline-offset-4 hover:text-primary">
              View your profile
            </Link>
            .
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={logout} disabled={isLoggingOut}>
          <LogOutIcon />
          {isLoggingOut ? "Signing out..." : "Sign out"}
        </Button>
      </div>

      {error && <Error variant="modal" title="Failed to load your account" error={error} open={errorModalOpen} onOpenChange={setErrorModalOpen} onRetry={handleRetry} />}

      {/* Personal information */}
      <SettingsSection title="Profile" description="Your name is how you appear to everyone else in BugTracker - on tickets, comments and member lists.">
        {isPending || !user ? <SettingsFormSkeleton /> : <UpdateProfileForm user={user} />}
      </SettingsSection>

      {/* Email address */}
      <SettingsSection title="Email address" description="The address you sign in with and where BugTracker sends notifications and project invitations.">
        {isPending || !user ? <SettingsFormSkeleton /> : <UpdateEmailAddressForm user={user} />}
      </SettingsSection>

      {/* Password */}
      <SettingsSection title="Password" description="Update your password regularly and never reuse a password from another service.">
        {isPending || !user ? <SettingsFormSkeleton /> : <UpdatePasswordForm />}
      </SettingsSection>

      {/* Security overview */}
      <SettingsSection title="Security" description="A summary of how your account is protected. Additional options will appear here as they become available.">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <ShieldCheckIcon className="size-4 text-primary" />
              <CardTitle>Sign in &amp; security</CardTitle>
            </div>
            <CardDescription>How you currently access your account.</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <ul className="flex flex-col divide-y">
              <li className="flex items-center justify-between gap-4 px-8 py-3">
                <div className="flex items-center gap-2.5 text-xs text-muted-foreground">
                  <MailIcon className="size-3.5 shrink-0" />
                  Sign in method
                </div>
                <span className="text-right text-xs font-medium">Email and password</span>
              </li>
              <li className="flex items-center justify-between gap-4 px-8 py-3">
                <div className="flex items-center gap-2.5 text-xs text-muted-foreground">
                  <LockIcon className="size-3.5 shrink-0" />
                  Password requirements
                </div>
                <span className="text-right text-xs font-medium">8+ characters, mixed case, number, symbol</span>
              </li>
              <li className="flex items-center justify-between gap-4 px-8 py-3">
                <div className="flex items-center gap-2.5 text-xs text-muted-foreground">
                  <UserIcon className="size-3.5 shrink-0" />
                  Active session
                </div>
                <span className="text-right text-xs font-medium">This device</span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </SettingsSection>

      {/* Danger zone */}
      <SettingsSection title="Danger zone" description="Actions here are destructive and permanent. Proceed with caution - a deleted account cannot be restored.">
        <Card className="border-destructive/40">
          <CardHeader>
            <div className="flex items-center gap-2">
              <AlertTriangleIcon className="size-4 text-destructive" />
              <CardTitle className="text-destructive">Danger zone</CardTitle>
            </div>
            <CardDescription>Irreversible actions that permanently affect your account.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-6">
            <Alert variant="destructive">
              <AlertTriangleIcon />
              <AlertTitle>Deleting your account is permanent</AlertTitle>
              <AlertDescription>You will be removed from every project immediately and unassigned from all of your tickets. This action cannot be undone.</AlertDescription>
            </Alert>
            <div className="flex flex-col gap-4 border border-destructive/30 p-4">
              <div className="flex flex-col gap-1">
                <p className="text-sm font-semibold">Delete this account</p>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  Once you delete <span className="font-semibold text-foreground">{user ? [user.firstName, user.lastName].join(" ") : "your account"}</span>, there is no going back.
                  {overview && (
                    <>
                      {" "}
                      You will lose access to{" "}
                      <span className="font-semibold text-foreground">
                        {overview.stats.projectCount} {overview.stats.projectCount === 1 ? "project" : "projects"}
                      </span>{" "}
                      and be unassigned from{" "}
                      <span className="font-semibold text-foreground">
                        {overview.stats.ticketsAssigned} {overview.stats.ticketsAssigned === 1 ? "ticket" : "tickets"}
                      </span>
                      .
                    </>
                  )}
                </p>
              </div>
              <DeleteAccountForm stats={overview?.stats} />
            </div>
          </CardContent>
        </Card>
      </SettingsSection>
    </div>
  );
}
